import React from "react";

import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { useOverflowDetection } from "@replo/design-system/hooks/use-overflow-detection";
import twMerge from "@replo/design-system/utils/twMerge";
import { Checkbox } from "@v1/components/designSystem/checkbox/Checkbox";
import Description from "@v1/components/designSystem/label/Description";
import DeprecatedLabel from "@v1/components/designSystem/label/Label";
import pickBy from "lodash-es/pickBy";
import { Check, Image as ImageIcon } from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

type BaseMenuItemProps = {
  disabled?: boolean;
  layoutClassName?: string;
  UNSAFE_className?: string;
  onHover?: () => void;
  label?: string;
  description?: string;
  selected?: boolean;
  tooltipBehavior?: "always" | "overflow";
  tooltip?: React.ReactNode;
};

type DefaultVariant = {
  variant: "default";
  startEnhancer?: React.ReactNode;
};

type IconVariant = {
  variant: "icon";
  startEnhancer: React.ReactNode;
};

type ProductVariant = {
  variant: "product";
  startEnhancer?: React.ReactNode;
  hasCheckbox?: boolean;
  tag?: React.ReactNode;
  productImage?: string;
};

type FlexibleVariant = {
  variant: "flexible";
  startEnhancer?: React.ReactNode;
  endEnhancer?: React.ReactNode;
};

type VariantProps = {
  startEnhancer?: React.ReactNode;
  endEnhancer?: React.ReactNode;
  hasCheckbox?: boolean;
  tag?: React.ReactNode;
  productImage?: string;
};

type LargeMenuItemProps = BaseMenuItemProps &
  (DefaultVariant | ProductVariant | IconVariant | FlexibleVariant) &
  React.ButtonHTMLAttributes<HTMLButtonElement>;

// NOTE (Jackson, 2025-03-28): We should not pass custom props directly on the button element as React
// will throw console errors (these are not valid DOM attributes).
const CUSTOM_PROPS = new Set([
  "variant",
  "layoutClassName",
  "UNSAFE_className",
  "onHover",
  "startEnhancer",
  "endEnhancer",
  "hasCheckbox",
  "tag",
  "productImage",
]);

const LargeMenuItem = React.forwardRef<HTMLButtonElement, LargeMenuItemProps>(
  (props, ref) => {
    const [descriptionRef, isOverflowingDescription] = useOverflowDetection();
    const [labelRef, isOverflowingLabel] = useOverflowDetection();

    const {
      disabled = false,
      layoutClassName,
      UNSAFE_className,
      variant,
      onHover,
      label,
      description,
      selected,
      ...restProps
    } = props;

    // Filter out custom props that shouldn't be passed to DOM
    const domProps = pickBy(restProps, (_, key) => !CUSTOM_PROPS.has(key));

    const variantProps = exhaustiveSwitch(
      props,
      "variant",
    )<VariantProps>({
      default: (props) => ({
        startEnhancer: props.startEnhancer,
      }),
      product: (props) => ({
        startEnhancer: props.startEnhancer,
        hasCheckbox: props.hasCheckbox,
        tag: props.tag,
        productImage: props.productImage,
      }),
      icon: (props) => ({
        startEnhancer: props.startEnhancer,
      }),
      flexible: (props) => ({
        startEnhancer: props.startEnhancer,
        endEnhancer: props.endEnhancer,
      }),
    });

    const content = (
      <button
        ref={ref}
        role="menuitem"
        className={twMerge(
          "hover:bg-selectable-hover relative flex w-full shrink cursor-pointer gap-[6px] rounded-md px-2 py-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent",
          variant !== "flexible" && "h-12",
          variant === "product" && "items-center",
          selected &&
            "bg-selectable-selected hover:bg-selectable-selected-hover",
          layoutClassName,
          UNSAFE_className,
        )}
        disabled={disabled}
        onMouseEnter={onHover}
        {...domProps}
      >
        {variantProps.startEnhancer && (
          <div
            className={twMerge(
              variant === "icon" &&
                "bg-surface flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-xs",
            )}
          >
            {variantProps.startEnhancer}
          </div>
        )}
        {variant === "product" && (
          <div className="border-0.5 border-border h-8 w-8 shrink-0 rounded-md bg-white">
            {variantProps.productImage ? (
              <img
                src={variantProps.productImage}
                className="h-full w-full rounded-md object-contain"
                loading="eager"
                alt=""
              />
            ) : (
              // image placeholder
              <div className="bg-surface flex h-full w-full items-center justify-center rounded-md">
                <ImageIcon className="text-muted size-4" />
              </div>
            )}
          </div>
        )}
        <div className="flex min-w-0 grow cursor-pointer flex-col text-left">
          <DeprecatedLabel
            size="sm"
            label={label}
            ref={labelRef}
            truncate
            pointer
          />
          {variantProps.tag ? (
            <div className="flex items-center gap-1">
              {variantProps.tag}
              <Description
                size="sm"
                description={description}
                // Craig (2025-03-05): Adding this so that the tag and description are aligned vertically.
                // Otherwise it is off center.
                UNSAFE_className="leading-none truncate"
                type="muted"
                ref={descriptionRef}
              />
            </div>
          ) : (
            <Description
              size="sm"
              description={description}
              layoutClassName="m-0"
              type="muted"
              UNSAFE_className={twMerge(variant !== "flexible" && "truncate")}
              ref={descriptionRef}
            />
          )}
        </div>
        {variantProps.hasCheckbox && <Checkbox checked={selected} />}
        {selected && !variantProps.hasCheckbox && (
          <div className="flex h-full items-center justify-center text-base">
            <Check className="icon-size-default" />
          </div>
        )}
        {variant === "flexible" && variantProps.endEnhancer && (
          <div className="flex items-center">{variantProps.endEnhancer}</div>
        )}
      </button>
    );
    if (
      isOverflowingDescription ||
      isOverflowingLabel ||
      props.tooltipBehavior === "always"
    ) {
      const tooltipContent = props.tooltip ?? (
        <div className="flex w-[250px] flex-col gap-1">
          <p className="font-medium">{label}</p>
          <p>{description}</p>
        </div>
      );
      return (
        <Tooltip content={tooltipContent} triggerAsChild>
          {content}
        </Tooltip>
      );
    } else {
      return content;
    }
  },
);

LargeMenuItem.displayName = "LargeMenuItem";

export { LargeMenuItem };
